/**
 * The application shell (design §7.2, §7.3; requirements B4, N-1, A2).
 *
 * One build serves every role. Navigation is driven by the permissions `/me`
 * resolved, so a technician never sees the storekeeper's screens — and the same
 * bundle needs no per-role variant.
 *
 * Mobile-first: a bottom tab bar within thumb reach on a phone, a sidebar from
 * `md` up. No horizontal page scroll at any width (§7.3).
 */

import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';

import { NotificationBell } from '../features/notifications/NotificationBell';
import { OfflineBanner } from '../offline/OfflineBanner';
import { cn } from './ui/cn';
import { PoweredBy } from '../features/auth/CompanyBrand';
import { useBranding } from '../features/auth/branding';
import { CompanyMark } from './CompanyMark';
import { Banner, Button } from './ui';
import { Breadcrumbs, CrumbProvider } from './ui/breadcrumbs';
import { PERM, type Permission } from '../auth/permissions';
import { useSession } from '../auth/session';
import { useList } from '../api/hooks';
import { useDayAccess } from '../features/attendance/access';
import { approvalsAdmitted } from '../features/dispatch/approvalAccess';

const APPROVAL_PERMS: Permission[] = [
  PERM.GATE_OUT_APPROVE,
  PERM.DISPOSAL_APPROVE,
  PERM.PROJECT_VIEW_COST,
];

interface NavItem {
  to: string;
  label: string;
  /** Shown only if the user holds one of these. Absent means always shown. */
  anyOf?: Permission[];
  icon: ReactNode;
}

// Kept in one list so the phone tab bar and the desktop sidebar can never drift
// apart. Order matters: the first four visible items become the phone tabs (E7).
const NAV_ITEMS: NavItem[] = [
  { to: '/', label: 'Home', icon: <GlyphHome /> },
  {
    to: '/gate-in',
    label: 'Gate-in',
    anyOf: [PERM.GATE_IN_POST],
    icon: <GlyphInbox />,
  },
  {
    to: '/gate-out',
    label: 'Gate-out',
    anyOf: [PERM.GATE_OUT_REQUEST, PERM.GATE_OUT_RELEASE],
    icon: <GlyphTruck />,
  },
  // E7: Stock sits ahead of Approvals so the phone bar's four tabs are Home,
  // Gate-in, Gate-out, Stock — the things touched all day. Approvals moves under
  // More, where a count badge says when it needs a look.
  { to: '/stock', label: 'Stock', icon: <GlyphBoxes /> },
  // R1–R3: anyone in the field spends, asks for an allowance or registers a
  // casual, so no permission gates it. Lands under More on a phone.
  { to: '/money', label: 'Money', icon: <GlyphCoins /> },
  // R13: everyone in the field clocks in, so no permission gates it.
  { to: '/time', label: 'Time', icon: <GlyphClock /> },
  // R14 (§4.20.10): every member reads the register; writes are gated inside.
  { to: '/assets', label: 'Assets', icon: <GlyphWrench /> },
  {
    // Everything waiting on one person: gate passes, disposals, and — since
    // O16 — project expenses and closeout costs. A manager who approves only
    // the last two still needs the entry, hence `project.view_cost` here.
    to: '/approvals',
    label: 'Approvals',
    anyOf: APPROVAL_PERMS,
    icon: <GlyphCheck />,
  },
  {
    // O12: a manager's standing question is "how is my PO doing", and it is
    // asked often enough to be top-level rather than buried under settings.
    to: '/projects',
    label: 'Projects',
    anyOf: [PERM.PROJECT_VIEW_COST, PERM.CATALOGUE_MANAGE, PERM.PROJECT_MANAGE],
    icon: <GlyphClipboard />,
  },
  {
    to: '/jobs',
    label: 'Jobs',
    anyOf: [PERM.JOB_CLOSEOUT, PERM.JOB_MANAGE, PERM.REPORT_VIEW_ALL],
    icon: <GlyphClipboard />,
  },
  {
    // S16: the write-off screen had a route and no way in.
    to: '/disposals',
    label: 'Disposals',
    anyOf: [PERM.STOCK_ADJUST, PERM.DISPOSAL_APPROVE],
    icon: <GlyphShield />,
  },
  {
    // J1, J2: quarantine has its own entry because material sitting there
    // unnoticed is the failure the requirement exists to prevent.
    to: '/quarantine',
    label: 'Quarantine',
    anyOf: [PERM.STOCK_ADJUST, PERM.GATE_IN_POST],
    icon: <GlyphShield />,
  },
  {
    // K1, K3: an operator audit opens with this screen.
    to: '/client-material',
    label: 'Client stock',
    anyOf: [PERM.GATE_OUT_RELEASE, PERM.REPORT_VIEW_ALL, PERM.GATE_OUT_APPROVE],
    icon: <GlyphHandshake />,
  },
  {
    // T5.11, T5.12: an owner's two standing questions — who has it, and what is
    // still unresolved. Both are top-level because both are checked daily.
    to: '/custody',
    label: 'Custody',
    anyOf: [PERM.REPORT_VIEW_ALL, PERM.GATE_OUT_APPROVE],
    icon: <GlyphHand />,
  },
  {
    to: '/exceptions',
    label: 'Exceptions',
    anyOf: [PERM.GATE_OUT_APPROVE, PERM.STOCK_ADJUST, PERM.REPORT_VIEW_ALL],
    icon: <GlyphAlert />,
  },
  {
    to: '/reports',
    label: 'Reports',
    anyOf: [PERM.REPORT_VIEW_ALL],
    icon: <GlyphChart />,
  },
  {
    to: '/settings',
    label: 'Settings',
    // No `anyOf`: everybody has a security pane of their own (B5, T8.10), and
    // the admin panes inside are gated individually.
    icon: <GlyphCog />,
  },
];

/**
 * What this person is, in the company's own words.
 *
 * A name alone does not tell somebody what they are allowed to do, and "Brian
 * Store Keeper" is a name that happens to read like a job — which is exactly
 * the confusion worth removing. Several roles are listed, because somebody who
 * is both a storekeeper and an approver needs to know they are both.
 */
function roleSummary(roles: { name: string }[] | undefined): string {
  if (!roles?.length) return 'No role yet';
  if (roles.length <= 2) return roles.map((role) => role.name).join(' · ');
  return `${roles[0].name} and ${roles.length - 1} more`;
}

/**
 * Split in two only so the provider can sit above the shell without indenting
 * the whole of it: the crumb labels are published by the screens inside the
 * outlet, so the store has to be above them.
 */
export function AppShell() {
  return (
    <CrumbProvider>
      <AppShellInner />
    </CrumbProvider>
  );
}

function AppShellInner() {
  const { user, logout, hasAny, isSuspended } = useSession();

  // S19: Approvals also admits a person with a day waiting on them or a project
  // to manage, who may hold no approval permission at all.
  const { awaitingCount, managesProject } = useDayAccess();
  const canApprove = approvalsAdmitted({
    holdsApprovalPermission: hasAny(...APPROVAL_PERMS),
    awaitingCount,
    managesProject,
  });

  const visible = NAV_ITEMS.filter((item) =>
    item.to === '/approvals' ? canApprove : !item.anyOf || hasAny(...item.anyOf),
  );

  // U2 (§4.23.1): the company's own mark, by address. If the lookup failed, the
  // name on the signed-in user still stands in; no company at all is the platform
  // console, which keeps the YardFlow marks.
  const branding = useBranding();
  const companyName = branding.data?.name ?? user?.organization?.name ?? null;
  const logoUrl = branding.data?.logo_url ?? null;

  // E7: how many approvals are waiting. Only asked of people who can act on
  // them; `page_size: 1` because only `count` is wanted. The app turns window
  // focus refetching off globally, but coming back to the tab is exactly when a
  // count like this has gone stale, so it is switched on here.
  const pending = useList<{ id: number }>(
    'approvals/pending',
    { page_size: 1 },
    { enabled: canApprove, refetchOnWindowFocus: true },
  );
  const pendingCount = canApprove ? (pending.data?.count ?? 0) : 0;

  return (
    <div className="flex min-h-full min-h-dvh flex-col bg-slate-50 md:flex-row">

      {/* Sidebar from md up.
          `sticky top-0 h-screen`: navigation stays put while the content beside
          it scrolls. A long stock list used to carry the whole sidebar off the
          top of the window, so getting to another screen meant scrolling back up
          first — on the layout where there is most room to keep it. */}
      <aside className="sticky top-0 hidden h-screen w-60 shrink-0 border-r border-slate-200 bg-white md:flex md:flex-col">
        <div className="px-4 py-5">
          <CompanyMark name={companyName} logoUrl={logoUrl} size="sidebar" />
        </div>
        {/* The nav itself scrolls, and only if it has to: a role with twelve
            destinations still fits a laptop, but the sidebar must never be the
            reason something is unreachable. */}
        <nav className="quiet-scroll flex flex-1 flex-col gap-1 overflow-y-auto px-2">
          {visible.map((item) => (
            <SidebarLink
              key={item.to}
              item={item}
              badge={item.to === '/approvals' ? pendingCount : 0}
            />
          ))}
        </nav>
        {/* Pinned to the bottom of the sidebar rather than to the end of a long
            nav list — sign out should be in the same place on every screen. */}
        <div className="shrink-0 border-t border-slate-200 p-3">
          <div className="flex items-center justify-between gap-2 pb-2">
            <div className="min-w-0 px-1">
              <p className="truncate text-sm text-slate-600">{user?.full_name}</p>
              <p className="truncate text-xs text-slate-500">{roleSummary(user?.roles)}</p>
            </div>
            {/* L1: in-app is the channel that is always on, so the badge is
                visible from every screen rather than only on its own page. */}
            <NotificationBell />
          </div>
          <Button variant="secondary" block onClick={() => void logout()}>
            Sign out
          </Button>
          {/* U2: the company's logo is the face of the app; YardFlow is credited
              small, last, and not at all on the platform console. */}
          {companyName ? <PoweredBy className="mt-3" /> : null}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* §8.1: unmistakable, and at the top of the content rather than
            floating over it. A fixed overlay would cover the first line of every
            screen — and covering content to announce a problem is its own
            problem. */}
        <div className="sticky top-0 z-20">
          <OfflineBanner />
        </div>

        {/* Phone header. */}
        <header className="flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 md:hidden">
          <div className="flex min-w-0 items-center gap-2.5">
            {/* U2 (amended): on a phone the header is the company's logo alone —
                it already says whose system this is, and Home greets the person
                by name and role. The console keeps the YardFlow mark and says so. */}
            <CompanyMark name={companyName} logoUrl={logoUrl} size="header" />
            {companyName ? null : (
              <p className="truncate text-base font-semibold text-slate-900">
                Platform administration
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <NotificationBell />
            <Button variant="ghost" onClick={() => void logout()}>
              Sign out
            </Button>
          </div>
        </header>

        {isSuspended ? (
          <div className="px-4 pt-4">
            {/* A2: the notice the requirement asks for. Reads still work. */}
            <Banner tone="warning">
              This organization is suspended. You can still view records, but new
              movements cannot be posted. Please contact support.
            </Banner>
          </div>
        ) : null}

        {/*
          A flex column so a tabbed screen can stretch to the bottom edge: a
          swipe is read by the screen's wrapper, and on a short page a thumb
          below the content must still land on it.
        */}
        <main className="flex min-w-0 flex-1 flex-col px-4 py-4 md:px-6 md:py-6">
          {/*
            P2: one trail for every screen, derived from the URL. Putting it in
            `PageHeader` instead would mean twenty-six screens each stating
            their own ancestry, and the first renamed route leaving behind a
            crumb that lies about where you are.
          */}
          <Breadcrumbs />
          <Outlet />
        </main>

        {/* Bottom tab bar on a phone — thumb-reachable. */}
        <PhoneTabBar items={visible} approvalsWaiting={pendingCount} />
      </div>
    </div>
  );
}

/** A small count pill; nothing at zero. The label is what a screen reader hears. */
function CountBadge({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      aria-label={`${count} waiting`}
      className={cn(
        'inline-flex min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[11px] leading-[18px] font-semibold text-white',
        className,
      )}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function SidebarLink({ item, badge = 0 }: { item: NavItem; badge?: number }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        cn(
          'flex min-h-[44px] items-center gap-3 rounded-lg px-3 text-sm font-medium',
          isActive ? 'bg-slate-900 text-white' : 'text-slate-700 hover:bg-slate-100',
        )
      }
    >
      <span aria-hidden="true">{item.icon}</span>
      {item.label}
      <CountBadge count={badge} className="ml-auto" />
    </NavLink>
  );
}

/**
 * The phone navigation (§7.3; N-1).
 *
 * A bottom bar fits about five targets at 44px on the narrowest phone this has to
 * work on, and an owner has thirteen destinations. The bar used to render
 * `items.slice(0, 5)` — which quietly made the other eight *unreachable on a
 * phone*: no Reports, no Settings, no Exceptions, no Quarantine, from the device
 * this product is built around. The sidebar has them all, so it only showed up
 * for somebody working on a phone, which is everybody in a yard.
 *
 * So: four tabs, then More. The tabs are the first four `NAV_ITEMS` the person
 * can see, which the list orders as Home, Gate-in, Gate-out, Stock (E7) — what a
 * yard touches all day. Approvals is therefore under More for an owner, and a
 * waiting-count badge on More (and on the row) keeps it from being forgotten.
 * Everything else is one tap away in
 * a sheet, at full width and full height, which is easier to hit than a fifth
 * cramped tab was.
 *
 * More is highlighted while the current screen lives inside it, so the bar never
 * shows nothing as selected.
 */
const PRIMARY_TABS = 4;

function PhoneTabBar({ items, approvalsWaiting }: { items: NavItem[]; approvalsWaiting: number }) {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // With one to spare there is no point spending a slot on More.
  const overflows = items.length > PRIMARY_TABS + 1;
  const primary = overflows ? items.slice(0, PRIMARY_TABS) : items;
  const rest = overflows ? items.slice(PRIMARY_TABS) : [];

  // Navigating closes the sheet. Without this it stays open over the screen it
  // just opened, and the first thing you do is dismiss it.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  // The badge goes where Approvals actually is: the sheet's button, or its tab.
  const approvalsInRest = rest.some((item) => item.to === '/approvals');

  const restIsActive = rest.some(
    (item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`),
  );

  return (
    <>
      {open ? (
        <>
          {/* Tapping away closes it — the cheapest dismissal on a phone, and the
              one people try first. */}
          <button
            type="button"
            aria-label="Close menu"
            className="fixed inset-0 z-30 bg-slate-900/40 md:hidden"
            onClick={() => setOpen(false)}
          />
          <div
            id="more-menu"
            role="dialog"
            aria-modal="true"
            aria-label="More screens"
            // Above the bar, not over it: the way out stays visible and in the
            // same place, so More toggles rather than traps.
            className="fixed inset-x-0 bottom-[calc(56px+env(safe-area-inset-bottom))] z-40 max-h-[70vh] overflow-y-auto border-t border-slate-200 bg-white pb-2 md:hidden"
          >
            <p className="px-4 pt-3 pb-1 text-xs font-medium tracking-wide text-slate-500 uppercase">
              More
            </p>
            {rest.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex min-h-[52px] items-center gap-3 px-4 text-base',
                    isActive ? 'bg-slate-100 font-medium text-slate-900' : 'text-slate-700',
                  )
                }
              >
                <span aria-hidden="true">{item.icon}</span>
                {item.label}
                <CountBadge
                  count={item.to === '/approvals' ? approvalsWaiting : 0}
                  className="ml-auto"
                />
              </NavLink>
            ))}
          </div>
        </>
      ) : null}

      <nav
        className={cn(
          'sticky bottom-0 z-20 flex border-t border-slate-200 bg-white pb-safe md:hidden',
          // Above the backdrop while the sheet is open. Otherwise the dimming
          // falls over the bar too: it reads as disabled, and the More button
          // that closes it is behind the overlay.
          open && 'z-50',
        )}
      >
        {primary.map((item) => (
          <TabLink
            key={item.to}
            item={item}
            badge={item.to === '/approvals' ? approvalsWaiting : 0}
          />
        ))}
        {overflows ? (
          <button
            type="button"
            aria-expanded={open}
            aria-controls="more-menu"
            onClick={() => setOpen((wasOpen) => !wasOpen)}
            className={cn(
              'relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 text-xs',
              open || restIsActive ? 'text-slate-900' : 'text-slate-500',
            )}
          >
            <span aria-hidden="true" className="relative">
              <GlyphMore />
            </span>
            <span className="truncate px-1">More</span>
            <CountBadge
              count={approvalsInRest ? approvalsWaiting : 0}
              className="absolute top-1 left-1/2 ml-2"
            />
          </button>
        ) : null}
      </nav>
    </>
  );
}

function TabLink({ item, badge = 0 }: { item: NavItem; badge?: number }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }) =>
        cn(
          'relative flex min-h-[56px] flex-1 flex-col items-center justify-center gap-1 text-xs',
          isActive ? 'text-slate-900' : 'text-slate-500',
        )
      }
    >
      <span aria-hidden="true">{item.icon}</span>
      <span className="truncate px-1">{item.label}</span>
      <CountBadge count={badge} className="absolute top-1 left-1/2 ml-2" />
    </NavLink>
  );
}

/* Inline SVG rather than an icon package: it keeps the bundle small on a
   cellular connection and needs no network at render time. */

function glyph(path: ReactNode) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {path}
    </svg>
  );
}

function GlyphHome() {
  return glyph(<><path d="M3 10.5 12 3l9 7.5" /><path d="M5 9.5V21h14V9.5" /></>);
}
function GlyphInbox() {
  return glyph(<><path d="M3 12h5l2 3h4l2-3h5" /><path d="M4 12 6 4h12l2 8v8H4z" /></>);
}
function GlyphTruck() {
  return glyph(<><path d="M2 7h11v9H2z" /><path d="M13 10h4l4 3v3h-8z" /><circle cx="6" cy="18" r="2" /><circle cx="17" cy="18" r="2" /></>);
}
function GlyphCheck() {
  return glyph(<><path d="M20 6 9 17l-5-5" /></>);
}
function GlyphBoxes() {
  return glyph(<><path d="M3 8h8v8H3zM13 8h8v8h-8z" /><path d="M3 8l4-4h8l4 4" /></>);
}
function GlyphWrench() {
  return glyph(<><path d="M14.5 6.5a4 4 0 0 0 5 5L10 21l-3-3z" /><path d="M14.5 6.5 17 9" /></>);
}
function GlyphCoins() {
  return glyph(<><circle cx="9" cy="9" r="5" /><path d="M14 6.3A5 5 0 0 1 14 15.7M9 7v4M7.5 9h3" /><path d="M5 14.5V18c0 1.1 2 2 4.5 2 2 0 3.700-.5 4.300-1.300" /></>);
}
function GlyphClock() {
  return glyph(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>);
}
function GlyphClipboard() {
  return glyph(<><path d="M9 4h6v3H9z" /><path d="M6 6h2v0h8V6h2v15H6z" /><path d="M9 12h6M9 16h4" /></>);
}
function GlyphChart() {
  return glyph(<><path d="M4 20V4" /><path d="M4 20h16" /><path d="M8 20v-6M13 20V8M18 20v-9" /></>);
}
function GlyphCog() {
  return glyph(<><circle cx="12" cy="12" r="3" /><path d="M12 3v3M12 18v3M4.5 7.5l2 1M17.5 15.5l2 1M4.5 16.5l2-1M17.5 8.5l2-1" /></>);
}
function GlyphHand() {
  return glyph(<><path d="M8 13V5a1.5 1.5 0 0 1 3 0v6" /><path d="M11 11V4.5a1.5 1.5 0 0 1 3 0V11" /><path d="M14 11V6.5a1.5 1.5 0 0 1 3 0V14a6 6 0 0 1-6 6H9a5 5 0 0 1-5-5v-3a1.5 1.5 0 0 1 3 0" /></>);
}
function GlyphAlert() {
  return glyph(<><path d="M12 4 3 20h18z" /><path d="M12 10v5M12 17.5v.5" /></>);
}
function GlyphShield() {
  return glyph(<><path d="M12 3l7 3v6c0 4-3 7-7 9-4-2-7-5-7-9V6z" /><path d="M9.5 12l1.8 1.8L15 10" /></>);
}
function GlyphHandshake() {
  return glyph(<><path d="M3 12l4-4 5 5-4 4z" /><path d="M21 12l-4-4-5 5 4 4z" /><path d="M8 8l4-3 4 3" /></>);
}

function GlyphMore() {
  // Three dots: the one icon that reads as "the rest of it" without a label,
  // though it gets a label anyway.
  return glyph(
    <>
      <circle cx="5" cy="12" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="19" cy="12" r="1.5" />
    </>,
  );
}
